import {Nullable} from '../../app/TypeHelpers'
import {HKConstants} from '../../HKConstants'
import {StaySettings} from './AdminRegisterFacilityPage/StaySettingsComponent'
import {
    ACCESS_CONTROL_MANAGER,
    AVAILABLE_OR_NOT,
    CHARGE_UNIT,
    CLEANING_TIME,
    CodeMasterInterface,
    DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL,
    FACILITY_CATEGORY,
    HOURS,
    NUMBER_OF_TIMES_TO_STAY,
    PUBLISHING_SETTING,
    RELEASE_START_HOURS,
    RENTAL_TYPE,
    RESERVATION_ACCEPTABILITY_FOR_RESIDENT,
    RESERVATION_TIME_UNIT_MINUTES,
    RESERVATION_TIME_UNIT_MINUTES_NOT0,
    UNIT_OF_FACILITY_RESERVATION_RECEPTION_START_TIME,
    UNIT_TIME,
    VALID_OR_NOT,
} from '../../CodeMaster'
import {OptionSetting} from './AdminRegisterFacilityPage/FacilityOptionSetting/FacilityOptionInputForm'
import {ReservationSettings} from './AdminRegisterFacilityPage/ReservationSettingsComponent'
import {
    MaxReservationTimeByTimeZone,
    MaxReservationTimeInputFormProps,
} from './AdminRegisterFacilityPage/BusinessScheduleSetting/MaxReservationTimeInputForm/MaxReservationTimeInputForm'
import NewFacilityForOvernightRental, {StaySettingsForNewFacility} from './NewFacilityForOvernightRental'
import {createUUID, numberOrZero} from '../../stringHelper/stringHelpers'
import {valueStringOrNull} from '../../codeMasterHelper/CodeMasterHelper'
import {TimeTableItem} from './AdminRegisterFacilityPage/BusinessScheduleSetting/BusinessScheduleItemInterfaces'
import {ImageInformation} from '../shared/forms/PPImageInputWithPreview'
import NewFacilityCommon, {
    OptionSettingForNewFacility,
    ReservationSettingsForNewFacility,
    ServerImageInformation,
} from './NewFacilityCommon'
import NewFacilityForDurationRental, {
    BusinessSchedule,
    MaxReservationTimeSetting,
    ReservationTimeSection,
} from './NewFacilityForDurationRental'
import {formatServerTime} from '../../formatters/formatServerTime'
import FacilityDetails, {ImageLocation} from './FacilityDetails'
import {
    fromMomentToEndTime,
    fromMomentToStartTime,
} from './AdminRegisterFacilityPage/BusinessScheduleSetting/TimeTableTimeHelper'
import UpdateFacilityForOvernightRental from './UpdateFacilityForOvernightRental'
import UpdateFacilityForDurationRental from './UpdateFacilityForDurationRental'
import moment from 'moment'

export interface FacilityModelInitializeObserver {
    onInitialize(initData: FacilityModelDataSet): void
}

export interface NewFacilityModelObserver {
    onDataSetStateChange(completeToCreate: boolean): void

    onBuildOvernightRentalFacility(newFacility: NewFacilityForOvernightRental): void

    onBuildDurationRentalFacility(newFacility: NewFacilityForDurationRental): void
}

export interface UpdateFacilityModelObserver {
    onDataSetStateChange(completeToCreate: boolean): void

    onBuildOvernightRentalFacility(updateFacility: UpdateFacilityForOvernightRental): void

    onBuildDurationRentalFacility(updateFacility: UpdateFacilityForDurationRental): void
}

export interface FacilityModelDataSet {
    sortOrder: Nullable<number>
    facilityCategory: Nullable<FACILITY_CATEGORY>
    equipmentIds: number[]
    facilityName: string
    accessControlManager: Nullable<ACCESS_CONTROL_MANAGER>
    cardKeyFacilityNumber: Nullable<string>
    gateNumbers: string[]
    images: ImageInformation[]
    stayAvailability: AVAILABLE_OR_NOT,
    staySettings: StaySettings
    facilityIntroductory: string
    optionSettings: OptionSetting[]
    reservationSettings: ReservationSettings
    releaseSetting: PUBLISHING_SETTING
    releaseStartDate: Nullable<string>
    releaseStartTime: Nullable<RELEASE_START_HOURS>
    reserveBeforeCleaningTime: Nullable<CLEANING_TIME>
    reserveAfterCleaningTime: Nullable<CLEANING_TIME>
    maxReservationTimeSetting: MaxReservationTimeInputFormProps
    timeTableItems: TimeTableItem[]
}

export default class FacilityModel {
    protected newFacilityModelObservers: NewFacilityModelObserver[] = []
    protected updateFacilityModelObservers: UpdateFacilityModelObserver[] = []
    protected facilityModelInitializeObservers: FacilityModelInitializeObserver[] = []

    private sortOrder: Nullable<number> = null
    private facilityCategory: Nullable<FACILITY_CATEGORY> = null
    private equipmentIds: number[] = []
    private facilityName: string = ''
    private accessControlManager: Nullable<ACCESS_CONTROL_MANAGER> = null
    private cardKeyFacilityNumber: Nullable<string> = null
    private gateNumbers: string[] = []
    private images: ImageInformation[] = []
    private stayAvailability: RENTAL_TYPE = RENTAL_TYPE.OVERNIGHT
    private staySettings: StaySettings = {
        stayPossibleDateCount: null,
        stayContinueTimeRental: AVAILABLE_OR_NOT.UNAVAILABLE,
        checkinTimeHours: null,
        checkinTimeMinutes: null,
        checkoutTimeHours: null,
        checkoutTimeMinutes: null,
        stayCharge: null,
    }
    private facilityIntroductory: string = ''
    private optionSettings: OptionSetting[] = []
    private reservationSettings: ReservationSettings = {
        residentReserve: RESERVATION_ACCEPTABILITY_FOR_RESIDENT.BOTH,
        lotteryAvailability: AVAILABLE_OR_NOT.UNAVAILABLE,
        lotteryPriority: null,
        unitOfReserveReceptionStartTime: UNIT_OF_FACILITY_RESERVATION_RECEPTION_START_TIME.UNSPECIFIED,
        reserveReceptionStartMonth: 1,
        reserveReceptionStartDate: 1,
        unitOfReserveReceptionLimit: DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL.UNSPECIFIED,
        reserveReceptionLimitDate: 1,
        reserveReceptionLimitTimeHours: null,
        reserveReceptionLimitTimeMinutes: null,
        cancelAvailability: AVAILABLE_OR_NOT.AVAILABLE,
        unitOfCancelReceptionLimit: DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL.UNSPECIFIED,
        cancelReceptionLimitDate: 1,
        cancelReceptionLimitTimeHours: null,
        cancelReceptionLimitTimeMinutes: null,
    }
    private releaseSetting: PUBLISHING_SETTING = PUBLISHING_SETTING.PUBLIC
    private releaseStartDate: Nullable<string> = null
    private releaseStartTime: Nullable<RELEASE_START_HOURS> = null
    private reserveBeforeCleaningTime: Nullable<CLEANING_TIME> = null
    private reserveAfterCleaningTime: Nullable<CLEANING_TIME> = null
    private maxReservationTimeSetting: MaxReservationTimeInputFormProps = {
        isMultipleMaxReservationTime: false,
        singleMaxReservationTime: null,
        multipleMaxReservationTimes: [
            {startHour: null, endHour: null, maxTime: null},
            {startHour: null, endHour: null, maxTime: null},
            {startHour: null, endHour: null, maxTime: null},
            {startHour: null, endHour: null, maxTime: null},
            {startHour: null, endHour: null, maxTime: null},
        ],
        multipleMaxReservationUnitMinute: null,
    }
    private timeTableItems: TimeTableItem[] = []
    private version: Nullable<number> = null

    reset(): FacilityModel {
        Object.assign(this, new FacilityModel())
        return this
    }

    addNewFacilityModelObserver(observer: NewFacilityModelObserver): FacilityModel {
        this.newFacilityModelObservers = [observer, ...this.newFacilityModelObservers]
        return this
    }

    addUpdateFacilityModelObserver(observer: UpdateFacilityModelObserver): FacilityModel {
        this.updateFacilityModelObservers = [observer, ...this.updateFacilityModelObservers]
        return this
    }

    addFacilityModelInitializeObserver(observer: FacilityModelInitializeObserver): FacilityModel {
        this.facilityModelInitializeObservers = [observer, ...this.facilityModelInitializeObservers]
        return this
    }

    initialize(serverData: FacilityDetails | null): void {
        if (serverData !== null) {
            this.setServerData(serverData)
        }

        this.facilityModelInitializeObservers.forEach(
            (observer) => observer.onInitialize(this.getDataSet()),
        )
        this.notifyChangeToObservers()
    }

    getDataSet(): FacilityModelDataSet {
        return {
            sortOrder: this.sortOrder,
            facilityCategory: this.facilityCategory,
            equipmentIds: this.equipmentIds,
            facilityName: this.facilityName,
            accessControlManager: this.accessControlManager,
            cardKeyFacilityNumber: this.cardKeyFacilityNumber,
            gateNumbers: this.gateNumbers,
            images: this.images,
            reservationSettings: this.reservationSettings,
            releaseSetting: this.releaseSetting,
            releaseStartDate: this.releaseStartDate,
            releaseStartTime: this.releaseStartTime,
            facilityIntroductory: this.facilityIntroductory,
            optionSettings: this.optionSettings,
            reserveBeforeCleaningTime: this.reserveBeforeCleaningTime,
            reserveAfterCleaningTime: this.reserveAfterCleaningTime,
            stayAvailability: this.stayAvailability,
            staySettings: this.staySettings,
            maxReservationTimeSetting: this.maxReservationTimeSetting,
            timeTableItems: this.timeTableItems,
        }
    }

    setServerData(serverData: FacilityDetails): void {
        const dataSet: FacilityModelDataSet = this.convertFacilityDetailsToFacilityModelDataSet(serverData)

        this.sortOrder = dataSet.sortOrder
        this.facilityCategory = dataSet.facilityCategory
        this.equipmentIds = dataSet.equipmentIds
        this.facilityName = dataSet.facilityName
        this.accessControlManager = dataSet.accessControlManager
        this.cardKeyFacilityNumber = dataSet.cardKeyFacilityNumber
        this.gateNumbers = dataSet.gateNumbers
        this.images = dataSet.images
        this.reservationSettings = dataSet.reservationSettings
        this.releaseSetting = dataSet.releaseSetting
        this.releaseStartDate = dataSet.releaseStartDate
        this.releaseStartTime = dataSet.releaseStartTime
        this.facilityIntroductory = dataSet.facilityIntroductory
        this.optionSettings = dataSet.optionSettings
        this.reserveBeforeCleaningTime = dataSet.reserveBeforeCleaningTime
        this.reserveAfterCleaningTime = dataSet.reserveAfterCleaningTime
        this.stayAvailability = dataSet.stayAvailability
        this.staySettings = dataSet.staySettings
        this.maxReservationTimeSetting = dataSet.maxReservationTimeSetting
        this.timeTableItems = dataSet.timeTableItems
        this.version = serverData.version
    }

    private convertFacilityDetailsToFacilityModelDataSet(facilityDetails: FacilityDetails): FacilityModelDataSet {
        const {
            sortOrder,
            category,
            equipments,
            facilityName,
            accessControlManager,
            cardKeyFacilityNumber,
            gateNumbers,
            images,
            facilityIntroductory,
            optionSettings,
            releaseSetting,
            releaseStartDate,
            releaseStartTime,
            reserveBeforeCleaningTime,
            reserveAfterCleaningTime,
            staySetting,
            businessSchedules,
        } = facilityDetails

        return {
            sortOrder: sortOrder,
            facilityCategory: category,
            equipmentIds: equipments.map(equipment => equipment.id),
            facilityName: facilityName,
            accessControlManager: accessControlManager,
            cardKeyFacilityNumber: cardKeyFacilityNumber,
            gateNumbers: gateNumbers,
            images: this.convertImageLocation(images),
            stayAvailability: staySetting === null ? RENTAL_TYPE.DURATION : RENTAL_TYPE.OVERNIGHT,
            staySettings: staySetting === null ? {
                    ...this.createEmptyStaySetting(),
                } :
                {
                    stayPossibleDateCount:
                        NUMBER_OF_TIMES_TO_STAY.codeToEnumConstants(`${staySetting.stayPossibleDateCount}`),
                    stayContinueTimeRental: staySetting.stayContinueTimeRental,
                    checkinTimeHours: HOURS.codeToEnumConstants(`${staySetting.checkinTime.hours()}`),
                    checkinTimeMinutes:
                        RESERVATION_TIME_UNIT_MINUTES.codeToEnumConstants(`${staySetting.checkinTime.minutes()}`),
                    checkoutTimeHours: HOURS.codeToEnumConstants(`${staySetting.checkoutTime.hours()}`),
                    checkoutTimeMinutes:
                        RESERVATION_TIME_UNIT_MINUTES.codeToEnumConstants(`${staySetting.checkoutTime.minutes()}`),
                    stayCharge: staySetting.stayCharge,
                },
            facilityIntroductory:
                facilityIntroductory === null ? '' : facilityIntroductory,
            optionSettings: optionSettings.map(option => {
                const unitTime: Nullable<UNIT_TIME> = this.convertChargeUnitToUnitTime(option.chargeUnit)
                return {
                    localId: createUUID(),
                    optionId: option.optionId,
                    name: option.name,
                    sortOrder: option.sortOrder,
                    charge: option.charge,
                    isFixedCharge: unitTime === null ? VALID_OR_NOT.VALID : VALID_OR_NOT.NOT,
                    unitTimeHourValue: unitTime === UNIT_TIME.HOUR ? option.chargeUnitTime : null,
                    unitTimeMinutesValue: unitTime === UNIT_TIME.MINUTES ?
                        RESERVATION_TIME_UNIT_MINUTES_NOT0.codeToEnumConstants(`${option.chargeUnitTime}`) : null,
                    unitTimeSuffix: unitTime,
                    minQuantity: option.volumeMin,
                    maxQuantity: option.volumeMax,
                    quantitySuffix: option.unitName,
                    paymentType: option.paymentMethod,
                    required: option.appointRequired,
                }
            }),
            reservationSettings: this.convertServerReservationSetting(facilityDetails),
            releaseSetting: releaseSetting,
            releaseStartDate: releaseStartDate,
            releaseStartTime: releaseStartTime,
            reserveBeforeCleaningTime:
                CLEANING_TIME.codeToEnumConstants(`${reserveBeforeCleaningTime}`),
            reserveAfterCleaningTime:
                CLEANING_TIME.codeToEnumConstants(`${reserveAfterCleaningTime}`),
            maxReservationTimeSetting: this.convertServerMaxReservationTimeSetting(facilityDetails),
            timeTableItems: businessSchedules === null ? [] :
                businessSchedules.map(schedule => {
                    return {
                        id: createUUID(),
                        title: '営業',
                        businessScheduleUnit: schedule.businessScheduleUnit,
                        dateStraddle: schedule.dateStraddle,
                        joinPrevDaysSchedule: schedule.joinPrevDaysSchedule,
                        startTime: schedule.startTime === null ? null : fromMomentToStartTime(schedule.startTime),
                        endTime: schedule.endTime === null ? null : fromMomentToEndTime(schedule.endTime),
                        charge: schedule.charge,
                        chargeUnit: schedule.chargeUnit,
                        chargeUnitTime: schedule.chargeUnitTime,
                        changedChargeApplyStartTime: schedule.changedChargeApplyStartTime,
                        changedChargeApplyStartTimeUnit: schedule.changedChargeApplyStartTimeUnit,
                        changedCharge: schedule.changedCharge,
                        changedChargeUnit: schedule.changedChargeUnit,
                        changedChargeUnitTime: schedule.changedChargeUnitTime,
                    }
                }),
        }
    }

    private convertServerMaxReservationTimeSetting(facilityDetails: FacilityDetails)
        : MaxReservationTimeInputFormProps {
        const {maxReservationTimeSetting} = facilityDetails
        const props = this.createEmptyMaxReservationTimeInputFormProps()
        if (maxReservationTimeSetting === null) {
            return props
        }

        const {allDayMaxReservationTime, reservationTimeSections} = maxReservationTimeSetting

        if (reservationTimeSections !== null && reservationTimeSections.length > 0) {
            const minutes = RESERVATION_TIME_UNIT_MINUTES.codeToEnumConstants(
                `${reservationTimeSections[0].startTime.minute()}`,
            )

            const convertedTimeZones: MaxReservationTimeByTimeZone[] =
                reservationTimeSections.map((section, index, sections): MaxReservationTimeByTimeZone => {
                    return {
                        startHour: HOURS.codeToEnumConstants(`${sections[index].startTime.hour()}`),
                        endHour:
                            HOURS.codeToEnumConstants(`${sections[(index + 1) % sections.length].startTime.hour()}`),
                        maxTime: sections[index].maxReservationTime,
                    }
                })
            const multipleMaxReservationTimes = [
                ...convertedTimeZones,
                ...props.multipleMaxReservationTimes,
            ].slice(0, 5)

            props.isMultipleMaxReservationTime = true
            props.multipleMaxReservationTimes = multipleMaxReservationTimes
            props.multipleMaxReservationUnitMinute = minutes
        } else {
            props.isMultipleMaxReservationTime = false
            props.singleMaxReservationTime = allDayMaxReservationTime
        }

        return props
    }

    private createEmptyStaySetting(): StaySettings {
        return {
            stayPossibleDateCount: null,
            stayContinueTimeRental: AVAILABLE_OR_NOT.UNAVAILABLE,
            checkinTimeHours: null,
            checkinTimeMinutes: null,
            checkoutTimeHours: null,
            checkoutTimeMinutes: null,
            stayCharge: null,
        }
    }

    private createEmptyMaxReservationTimeInputFormProps(): MaxReservationTimeInputFormProps {
        return {
            isMultipleMaxReservationTime: false,
            singleMaxReservationTime: null,
            multipleMaxReservationTimes: [
                {startHour: null, endHour: null, maxTime: null},
                {startHour: null, endHour: null, maxTime: null},
                {startHour: null, endHour: null, maxTime: null},
                {startHour: null, endHour: null, maxTime: null},
                {startHour: null, endHour: null, maxTime: null},
            ],
            multipleMaxReservationUnitMinute: null,
        }
    }

    convertServerReservationSetting(facilityDetails: FacilityDetails): ReservationSettings {
        const {reservationSetting} = facilityDetails
        const {
            residentReserve,
            lotteryAvailability,
            lotteryPriority,
            reserveReceptionStartMonth,
            reserveReceptionStartDate,
            reserveReceptionLimitDate,
            reserveReceptionLimitTime,
            cancelAvailability,
            cancelReceptionLimitDate,
            cancelReceptionLimitTime,
        } = reservationSetting

        let unitOfReserveReceptionStartTime: UNIT_OF_FACILITY_RESERVATION_RECEPTION_START_TIME
        if (reserveReceptionStartMonth !== null) {
            unitOfReserveReceptionStartTime = UNIT_OF_FACILITY_RESERVATION_RECEPTION_START_TIME.SPECIFIED_MONTH
        } else if (reserveReceptionStartDate !== null) {
            unitOfReserveReceptionStartTime = UNIT_OF_FACILITY_RESERVATION_RECEPTION_START_TIME.SPECIFIED_DAY
        } else {
            unitOfReserveReceptionStartTime = UNIT_OF_FACILITY_RESERVATION_RECEPTION_START_TIME.UNSPECIFIED
        }

        let unitOfReserveReceptionLimit: DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL
        if (reserveReceptionLimitDate !== null) {
            unitOfReserveReceptionLimit = DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL.SPECIFIED_DAY
        } else if (reserveReceptionLimitTime !== null) {
            unitOfReserveReceptionLimit = DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL.SPECIFIED_TIME
        } else {
            unitOfReserveReceptionLimit = DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL.UNSPECIFIED
        }

        let unitOfCancelReceptionLimit: DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL
        if (cancelAvailability === AVAILABLE_OR_NOT.UNAVAILABLE) {
            unitOfCancelReceptionLimit = DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL.UNSPECIFIED
        } else if (cancelReceptionLimitDate !== null) {
            unitOfCancelReceptionLimit = DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL.SPECIFIED_DAY
        } else if (cancelReceptionLimitTime !== null) {
            unitOfCancelReceptionLimit = DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL.SPECIFIED_TIME
        } else {
            unitOfCancelReceptionLimit = DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL.UNSPECIFIED
        }

        return {
            residentReserve: residentReserve,
            lotteryAvailability: lotteryAvailability,
            lotteryPriority: lotteryPriority,
            unitOfReserveReceptionStartTime: unitOfReserveReceptionStartTime,
            reserveReceptionStartMonth: reserveReceptionStartMonth,
            reserveReceptionStartDate: reserveReceptionStartDate,
            unitOfReserveReceptionLimit: unitOfReserveReceptionLimit,
            reserveReceptionLimitDate: reserveReceptionLimitDate,
            reserveReceptionLimitTimeHours:
                reserveReceptionLimitTime === null ? null : reserveReceptionLimitTime.hours(),
            reserveReceptionLimitTimeMinutes:
                reserveReceptionLimitTime === null ? null : reserveReceptionLimitTime.minutes(),
            cancelAvailability: cancelAvailability,
            unitOfCancelReceptionLimit: unitOfCancelReceptionLimit,
            cancelReceptionLimitDate: cancelReceptionLimitDate,
            cancelReceptionLimitTimeHours:
                cancelReceptionLimitTime === null ? null : cancelReceptionLimitTime.hours(),
            cancelReceptionLimitTimeMinutes:
                cancelReceptionLimitTime === null ? null : cancelReceptionLimitTime.minutes(),
        }
    }

    protected notifyChangeToObservers(): void {
        this.newFacilityModelObservers.forEach(
            (observer) => observer.onDataSetStateChange(this.checkDataSetCompleted()),
        )
        this.updateFacilityModelObservers.forEach(
            (observer) => observer.onDataSetStateChange(this.checkDataSetCompleted()),
        )
    }

    private checkDataSetCompleted(): boolean {
        if (!this.facilityCategory) {
            return false
        }

        if (this.equipmentIds.length === 0) {
            return false
        }

        if (this.facilityName === '') {
            return false
        }

        if (this.accessControlManager === ACCESS_CONTROL_MANAGER.ODEC_TECHNO && this.cardKeyFacilityNumber === null) {
            return false
        }

        if (this.accessControlManager === ACCESS_CONTROL_MANAGER.TEBRA_PASS && this.gateNumbers.length === 0) {
            return false
        }

        if (!this.checkReservationSettings(this.reservationSettings)) {
            return false
        }

        if (!this.staySettings) {
            return false
        }

        if (this.stayAvailability === RENTAL_TYPE.OVERNIGHT) {
            if ((this.staySettings.stayPossibleDateCount === null) ||
                (this.staySettings.checkinTimeHours === null) ||
                (this.staySettings.checkinTimeMinutes === null) ||
                (this.staySettings.checkoutTimeHours === null) ||
                (this.staySettings.checkoutTimeMinutes === null) ||
                (this.staySettings.stayCharge === null)) {
                return false
            }
        }

        if (this.stayAvailability === RENTAL_TYPE.DURATION &&
            !this.validateMaxReservationTimeSettings()) {
            return false
        }

        // noinspection RedundantIfStatementJS
        if (this.stayAvailability !== RENTAL_TYPE.OVERNIGHT) {
            if (this.timeTableItems.length === 0) {
                return false
            }
        }

        return true
    }

    private checkReservationSettings(reservationSettings: Nullable<ReservationSettings>): boolean {
        if (reservationSettings === null) {
            return false
        }
        if (reservationSettings.unitOfReserveReceptionStartTime ===
            UNIT_OF_FACILITY_RESERVATION_RECEPTION_START_TIME.SPECIFIED_MONTH &&
            reservationSettings.reserveReceptionStartMonth === null) {
            return false
        }
        if (reservationSettings.unitOfReserveReceptionStartTime ===
            UNIT_OF_FACILITY_RESERVATION_RECEPTION_START_TIME.SPECIFIED_DAY &&
            reservationSettings.reserveReceptionStartDate === null) {
            return false
        }
        if (reservationSettings.unitOfReserveReceptionLimit ===
            DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL.SPECIFIED_DAY &&
            reservationSettings.reserveReceptionLimitDate === null) {
            return false
        }
        if (reservationSettings.unitOfReserveReceptionLimit ===
            DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL.SPECIFIED_TIME && (
                reservationSettings.reserveReceptionLimitTimeHours === null ||
                reservationSettings.reserveReceptionLimitTimeMinutes === null)
        ) {
            return false
        }
        if (reservationSettings.cancelAvailability === AVAILABLE_OR_NOT.AVAILABLE) {
            if (reservationSettings.unitOfCancelReceptionLimit ===
                DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL.SPECIFIED_DAY &&
                reservationSettings.cancelReceptionLimitDate === null) {
                return false
            }
            if (reservationSettings.unitOfCancelReceptionLimit ===
                DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL.SPECIFIED_TIME && (
                    reservationSettings.cancelReceptionLimitTimeHours === null ||
                    reservationSettings.cancelReceptionLimitTimeMinutes === null)
            ) {
                return false
            }
        }
        return true
    }

    private validateMaxReservationTimeSettings(): boolean {
        if (this.maxReservationTimeSetting === null) {
            return false
        }

        const {
            isMultipleMaxReservationTime,
            singleMaxReservationTime,
            multipleMaxReservationTimes,
            multipleMaxReservationUnitMinute,
        } = this.maxReservationTimeSetting


        if (!isMultipleMaxReservationTime) {
            return this.validateSingleMaxReservationTime(singleMaxReservationTime)
        }

        return this.validateMultipleMaxReservationSettings(
            multipleMaxReservationTimes,
            multipleMaxReservationUnitMinute,
        )
    }

    private validateSingleMaxReservationTime(singleMaxReservationTime: Nullable<number>): boolean {
        return (singleMaxReservationTime !== null)
    }

    private validateMultipleMaxReservationSettings(
        timeZones: MaxReservationTimeByTimeZone[],
        minute: Nullable<RESERVATION_TIME_UNIT_MINUTES>,
    ): boolean {
        if (minute === null) {
            return false
        }

        if (timeZones.length === 0) {
            return false
        }

        const validStartEndItems: MaxReservationTimeByTimeZone[] =
            timeZones
                .filter(item => item.startHour !== null && item.endHour !== null)
        if (validStartEndItems.length === 0) {
            return false
        }

        const invalidMaxTimeItems: MaxReservationTimeByTimeZone[] =
            validStartEndItems.filter(item => item.maxTime === null)
        // noinspection RedundantIfStatementJS
        if (invalidMaxTimeItems.length > 0) {
            return false
        }

        return true
    }

    withSortOrder(sortOrder: Nullable<number>): FacilityModel {
        this.sortOrder = sortOrder
        this.notifyChangeToObservers()
        return this
    }

    withFacilityCategory(facilityCategory: Nullable<FACILITY_CATEGORY>): FacilityModel {
        this.facilityCategory = facilityCategory
        this.notifyChangeToObservers()
        return this
    }

    withEquipmentIds(equipmentIds: number[]): FacilityModel {
        this.equipmentIds = equipmentIds
        this.notifyChangeToObservers()
        return this
    }

    withFacilityName(facilityName: string): FacilityModel {
        this.facilityName = facilityName
        this.notifyChangeToObservers()
        return this
    }

    withAccessControlManager(accessControlManager: Nullable<ACCESS_CONTROL_MANAGER>): FacilityModel {
        this.accessControlManager = accessControlManager
        this.notifyChangeToObservers()
        return this
    }

    withCardKeyFacilityNumber(cardKeyFacilityNumber: Nullable<string>): FacilityModel {
        this.cardKeyFacilityNumber = cardKeyFacilityNumber
        this.notifyChangeToObservers()
        return this
    }

    withGateNumbers(gateNumbers: string[]): FacilityModel {
        this.gateNumbers = gateNumbers
        this.notifyChangeToObservers()
        return this
    }

    withImages(images: ImageInformation[]): FacilityModel {
        this.images = images
        this.notifyChangeToObservers()
        return this
    }

    withStayAvailability(stayAvailability: RENTAL_TYPE): FacilityModel {
        this.stayAvailability = stayAvailability
        this.notifyChangeToObservers()
        return this
    }

    withStaySettings(staySettings: StaySettings): FacilityModel {
        this.staySettings = staySettings
        this.notifyChangeToObservers()
        return this
    }

    withFacilityIntroductory(facilityIntroductory: string): FacilityModel {
        this.facilityIntroductory = facilityIntroductory
        this.notifyChangeToObservers()
        return this
    }

    withOptionSettings(optionSettings: OptionSetting[]): FacilityModel {
        this.optionSettings = optionSettings
        this.notifyChangeToObservers()
        return this
    }

    withReservationSettings(reservationSettings: ReservationSettings): FacilityModel {
        this.reservationSettings = reservationSettings
        this.notifyChangeToObservers()
        return this
    }

    withReleaseSetting(releaseSetting: PUBLISHING_SETTING): FacilityModel {
        this.releaseSetting = releaseSetting
        this.notifyChangeToObservers()
        return this
    }

    withReleaseStartDate(releaseStartDate: Nullable<string>): FacilityModel {
        this.releaseStartDate = releaseStartDate
        this.notifyChangeToObservers()
        return this
    }

    withReleaseStartTime(releaseStartTime: Nullable<RELEASE_START_HOURS>): FacilityModel {
        this.releaseStartTime = releaseStartTime
        this.notifyChangeToObservers()
        return this
    }

    withReserveBeforeCleaningTime(reserveBeforeCleaningTime: Nullable<CLEANING_TIME>): FacilityModel {
        this.reserveBeforeCleaningTime = reserveBeforeCleaningTime
        this.notifyChangeToObservers()
        return this
    }

    withReserveAfterCleaningTime(reserveAfterCleaningTime: Nullable<CLEANING_TIME>): FacilityModel {
        this.reserveAfterCleaningTime = reserveAfterCleaningTime
        this.notifyChangeToObservers()
        return this
    }

    withMaxReservationTimeSetting(maxReservationTimeSetting: MaxReservationTimeInputFormProps): FacilityModel {
        this.maxReservationTimeSetting = maxReservationTimeSetting
        this.notifyChangeToObservers()
        return this
    }

    withTimeTableItems(timeTableItems: TimeTableItem[]): FacilityModel {
        this.timeTableItems = timeTableItems
        this.notifyChangeToObservers()
        return this
    }

    build(): void {
        if (this.stayAvailability === RENTAL_TYPE.OVERNIGHT) {
            this.buildFacilityForOvernightRental()
        } else {
            this.buildFacilityForDurationRental()
        }
    }

    getCleansedStaySettings(): StaySettingsForNewFacility {
        const {
            stayPossibleDateCount,
            stayContinueTimeRental,
            checkinTimeHours,
            checkinTimeMinutes,
            checkoutTimeHours,
            checkoutTimeMinutes,
            stayCharge,
        } = this.staySettings

        const newStayPossibleDateCount = Number(getNotNullValue(stayPossibleDateCount).value)
        const checkinTime = this.formatTimeForSendServerFromCodeMasterInterface(checkinTimeHours, checkinTimeMinutes)
        const checkoutTime = this.formatTimeForSendServerFromCodeMasterInterface(checkoutTimeHours, checkoutTimeMinutes)
        const newStayCharge = Number(getNotNullValue(stayCharge))

        return {
            stayPossibleDateCount: newStayPossibleDateCount,
            stayContinueTimeRental: stayContinueTimeRental.value,
            checkinTime: checkinTime,
            checkoutTime: checkoutTime,
            stayCharge: newStayCharge,
        }
    }

    getChargeUnitForOptionCharge(
        isFixedCharge: VALID_OR_NOT,
        unitTimeHourValue: Nullable<number>,
        unitTimeMinutesValue: Nullable<RESERVATION_TIME_UNIT_MINUTES_NOT0>,
        unitTimeSuffix: Nullable<UNIT_TIME>,
    ): { chargeUnit: CHARGE_UNIT, chargeUnitTime: Nullable<number> } {
        if (isFixedCharge === VALID_OR_NOT.VALID) {
            return {chargeUnit: CHARGE_UNIT.FIXED, chargeUnitTime: null}
        }
        if (unitTimeSuffix === UNIT_TIME.HOUR) {
            return {chargeUnit: CHARGE_UNIT.TIME, chargeUnitTime: unitTimeHourValue}
        }
        return {chargeUnit: CHARGE_UNIT.MINUTE, chargeUnitTime: numberOrZero(valueStringOrNull(unitTimeMinutesValue))}
    }

    getCleansedImages(): ServerImageInformation[] {
        return this.images.map(image => ({
            fileNumber: image.fileNumber === undefined ? null : image.fileNumber,
            name: image.name,
            base64data: image.base64data,
            md5: image.md5,
        }))
    }

    getCleansedOptionSettings(): OptionSettingForNewFacility[] {
        return this.optionSettings.map((item: OptionSetting): OptionSettingForNewFacility => {
            const chargeUnit: { chargeUnit: CHARGE_UNIT, chargeUnitTime: Nullable<number> } =
                this.getChargeUnitForOptionCharge(
                    item.isFixedCharge,
                    item.unitTimeHourValue,
                    item.unitTimeMinutesValue,
                    item.unitTimeSuffix,
                )

            return {
                clientLocalId: item.localId,
                optionId: item.optionId,
                name: item.name,
                sortOrder: item.sortOrder,
                charge: item.charge,
                chargeUnitTime: chargeUnit.chargeUnitTime,
                chargeUnit: chargeUnit.chargeUnit.value,
                volumeMin: item.minQuantity,
                volumeMax: item.maxQuantity,
                unitName: item.quantitySuffix,
                paymentMethod: item.paymentType.value,
                appointRequired: item.required.value,
            }
        })
    }

    getCleansedReservationSettings(): ReservationSettingsForNewFacility {
        const {
            residentReserve,
            lotteryAvailability,
            lotteryPriority,
            unitOfReserveReceptionStartTime,
            reserveReceptionStartMonth,
            reserveReceptionStartDate,
            unitOfReserveReceptionLimit,
            reserveReceptionLimitDate,
            reserveReceptionLimitTimeHours,
            reserveReceptionLimitTimeMinutes,
            cancelAvailability,
            unitOfCancelReceptionLimit,
            cancelReceptionLimitDate,
            cancelReceptionLimitTimeHours,
            cancelReceptionLimitTimeMinutes,
        } = this.reservationSettings

        const newLotteryPriority =
            (lotteryAvailability === AVAILABLE_OR_NOT.AVAILABLE)
                ? getValueOrDefault(lotteryPriority, HKConstants.VMN.FAC.DEFAULT_PRIORITY_VALUE_OF_RESERVATIONS)
                : null

        const newReserveReceptionStartMonth =
            (unitOfReserveReceptionStartTime === UNIT_OF_FACILITY_RESERVATION_RECEPTION_START_TIME.SPECIFIED_MONTH)
                ? reserveReceptionStartMonth
                : null

        const newReserveReceptionStartDate =
            (unitOfReserveReceptionStartTime === UNIT_OF_FACILITY_RESERVATION_RECEPTION_START_TIME.SPECIFIED_DAY)
                ? reserveReceptionStartDate
                : null

        const newReserveReceptionLimitTime =
            (unitOfReserveReceptionLimit === DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL.SPECIFIED_TIME)
                ? formatServerTime(reserveReceptionLimitTimeHours, reserveReceptionLimitTimeMinutes)
                : null

        const newReserveReceptionLimitDate =
            (unitOfReserveReceptionLimit === DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL.SPECIFIED_DAY)
                ? reserveReceptionLimitDate
                : null

        const newCancelReceptionLimitDate =
            (cancelAvailability === AVAILABLE_OR_NOT.AVAILABLE &&
                unitOfCancelReceptionLimit === DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL.SPECIFIED_DAY)
                ? cancelReceptionLimitDate
                : null

        const newCancelReceptionLimitTime: Nullable<string> =
            (cancelAvailability === AVAILABLE_OR_NOT.AVAILABLE &&
                unitOfCancelReceptionLimit === DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL.SPECIFIED_TIME)
                ? formatServerTime(cancelReceptionLimitTimeHours, cancelReceptionLimitTimeMinutes)
                : null

        return {
            residentReserve: residentReserve.value,
            lotteryAvailability: lotteryAvailability.value,
            lotteryPriority: newLotteryPriority,
            reserveReceptionStartMonth: newReserveReceptionStartMonth,
            reserveReceptionStartDate: newReserveReceptionStartDate,
            reserveReceptionLimitDate: newReserveReceptionLimitDate,
            reserveReceptionLimitTime: newReserveReceptionLimitTime,
            cancelAvailability: cancelAvailability.value,
            cancelReceptionLimitDate: newCancelReceptionLimitDate,
            cancelReceptionLimitTime: newCancelReceptionLimitTime,
        }
    }

    getCleansedBusinessSchedule(timeTableItem: TimeTableItem): BusinessSchedule {
        const {
            businessScheduleUnit,
            dateStraddle,
            joinPrevDaysSchedule,
            startTime,
            endTime,
        } = timeTableItem
        let charge: Nullable<number> = null
        let chargeUnit: Nullable<CHARGE_UNIT> = null
        let chargeUnitTime: Nullable<number> = null
        let changedChargeApplyStartTime: Nullable<number> = null
        let changedChargeApplyStartTimeUnit: Nullable<UNIT_TIME> = null
        let changedCharge: Nullable<number> = null
        let changedChargeUnit: Nullable<CHARGE_UNIT> = null
        let changedChargeUnitTime: Nullable<number> = null

        const startTimeString = startTime !== null ?
            formatServerTime(startTime.hour.value, startTime.minute.value) : null
        const endTimeString = endTime !== null ?
            formatServerTime(endTime.hour.value, endTime.minute.value) : null

        if (joinPrevDaysSchedule !== VALID_OR_NOT.VALID) {
            charge = timeTableItem.charge
            chargeUnit = timeTableItem.chargeUnit
            chargeUnitTime = timeTableItem.chargeUnitTime

            if (timeTableItem.chargeUnit !== CHARGE_UNIT.FIXED) {
                changedChargeApplyStartTime = timeTableItem.changedChargeApplyStartTime
                changedChargeApplyStartTimeUnit = timeTableItem.changedChargeApplyStartTimeUnit
                changedCharge = timeTableItem.changedCharge
                changedChargeUnit = timeTableItem.changedChargeUnit
                changedChargeUnitTime = timeTableItem.changedChargeUnitTime
            }
        }

        return {
            businessScheduleUnit: businessScheduleUnit.value,
            dateStraddle: valueStringOrNull(dateStraddle),
            joinPrevDaysSchedule: valueStringOrNull(joinPrevDaysSchedule),
            startTime: startTimeString,
            endTime: endTimeString,
            charge: charge,
            chargeUnit: valueStringOrNull(chargeUnit),
            chargeUnitTime: chargeUnitTime,
            changedChargeApplyStartTime: changedChargeApplyStartTime,
            changedChargeApplyStartTimeUnit: valueStringOrNull(changedChargeApplyStartTimeUnit),
            changedCharge: changedCharge,
            changedChargeUnit: valueStringOrNull(changedChargeUnit),
            changedChargeUnitTime: changedChargeUnitTime,
        }
    }

    getCleansedMaxReservationTimeSetting(): MaxReservationTimeSetting {
        const {
            isMultipleMaxReservationTime,
            singleMaxReservationTime,
            multipleMaxReservationTimes,
            multipleMaxReservationUnitMinute,
        } = this.maxReservationTimeSetting

        if (isMultipleMaxReservationTime) {
            const reservationTimeSections: ReservationTimeSection[] = multipleMaxReservationTimes.map(
                (item): ReservationTimeSection | null => {
                    if (item.startHour === null ||
                        item.endHour === null ||
                        item.maxTime === null ||
                        multipleMaxReservationUnitMinute === null) {
                        return null
                    }
                    return {
                        startTime: formatServerTime(item.startHour.value, multipleMaxReservationUnitMinute.value),
                        maxReservationTime: item.maxTime,
                    }
                }).filter((item: ReservationTimeSection | null): item is ReservationTimeSection => item !== null)

            return {
                allDayMaxReservationTime: null,
                reservationTimeSections: reservationTimeSections,
            }
        } else {
            return {
                allDayMaxReservationTime: singleMaxReservationTime,
                reservationTimeSections: [],
            }
        }
    }

    buildFacilityCommon(): NewFacilityCommon {
        let releaseStartDate: Nullable<string> = null
        if (this.releaseStartDate !== null) {
            const startDateMoment: moment.Moment = moment(this.releaseStartDate)
            releaseStartDate = startDateMoment.format('YYYY/MM/DD')
        }
        return new NewFacilityCommon(
            getValueOrDefault(this.sortOrder, HKConstants.VMN.FAC.MAX_ORDER_OF_FACILITY_LIST_DISPLAY),
            valueStringOrNull(this.facilityCategory),
            this.equipmentIds,
            this.facilityName,
            this.accessControlManager?.value ?? null,
            this.accessControlManager === ACCESS_CONTROL_MANAGER.ODEC_TECHNO
                ? this.cardKeyFacilityNumber : null,
            this.accessControlManager === ACCESS_CONTROL_MANAGER.TEBRA_PASS
                ? this.gateNumbers : null,
            this.getCleansedImages(),
            this.facilityIntroductory,
            this.getCleansedOptionSettings(),
            this.getCleansedReservationSettings(),
            this.releaseSetting.value,
            this.releaseSetting.value === PUBLISHING_SETTING.PUBLIC.value ? null : releaseStartDate,
            this.releaseSetting.value === PUBLISHING_SETTING.PUBLIC.value ? null :
                this.releaseStartTime === null ? null : this.releaseStartTime.value,
            numberOrZero(valueStringOrNull(this.reserveBeforeCleaningTime)),
            numberOrZero(valueStringOrNull(this.reserveAfterCleaningTime)),
        )
    }

    buildFacilityForOvernightRental(): void {
        const facilityCommon: NewFacilityCommon = this.buildFacilityCommon()
        if (this.version !== null) {
            const updateFacility: UpdateFacilityForOvernightRental =
                new UpdateFacilityForOvernightRental(
                    facilityCommon.sortOrder,
                    facilityCommon.categoryId,
                    facilityCommon.equipmentIds,
                    facilityCommon.facilityName,
                    facilityCommon.accessControlManager,
                    facilityCommon.cardKeyFacilityNumber,
                    facilityCommon.gateNumbers,
                    facilityCommon.images,
                    facilityCommon.facilityIntroductory,
                    facilityCommon.optionSettings,
                    facilityCommon.reservationSetting,
                    facilityCommon.releaseSetting,
                    facilityCommon.releaseStartDate,
                    facilityCommon.releaseStartTime,
                    facilityCommon.reserveBeforeCleaningTime,
                    facilityCommon.reserveAfterCleaningTime,
                    this.getCleansedStaySettings(),
                    this.version,
                )
            this.updateFacilityModelObservers
                .forEach(observer => observer.onBuildOvernightRentalFacility(updateFacility))
        } else {
            const newFacility: NewFacilityForOvernightRental =
                new NewFacilityForOvernightRental(
                    facilityCommon.sortOrder,
                    facilityCommon.categoryId,
                    facilityCommon.equipmentIds,
                    facilityCommon.facilityName,
                    facilityCommon.accessControlManager,
                    facilityCommon.cardKeyFacilityNumber,
                    facilityCommon.gateNumbers,
                    facilityCommon.images,
                    facilityCommon.facilityIntroductory,
                    facilityCommon.optionSettings,
                    facilityCommon.reservationSetting,
                    facilityCommon.releaseSetting,
                    facilityCommon.releaseStartDate,
                    facilityCommon.releaseStartTime,
                    facilityCommon.reserveBeforeCleaningTime,
                    facilityCommon.reserveAfterCleaningTime,
                    this.getCleansedStaySettings(),
                )
            this.newFacilityModelObservers
                .forEach(observer => observer.onBuildOvernightRentalFacility(newFacility))
        }
    }

    buildFacilityForDurationRental(): void {
        const facilityCommon: NewFacilityCommon = this.buildFacilityCommon()
        if (this.version !== null) {
            const updateFacility: UpdateFacilityForDurationRental =
                new UpdateFacilityForDurationRental(
                    facilityCommon.sortOrder,
                    facilityCommon.categoryId,
                    facilityCommon.equipmentIds,
                    facilityCommon.facilityName,
                    facilityCommon.accessControlManager,
                    facilityCommon.cardKeyFacilityNumber,
                    facilityCommon.gateNumbers,
                    facilityCommon.images,
                    facilityCommon.facilityIntroductory,
                    facilityCommon.optionSettings,
                    facilityCommon.reservationSetting,
                    facilityCommon.releaseSetting,
                    facilityCommon.releaseStartDate,
                    facilityCommon.releaseStartTime,
                    facilityCommon.reserveBeforeCleaningTime,
                    facilityCommon.reserveAfterCleaningTime,
                    this.timeTableItems.map(timeTableItem => this.getCleansedBusinessSchedule(timeTableItem)),
                    this.getCleansedMaxReservationTimeSetting(),
                    this.version,
                )
            this.updateFacilityModelObservers
                .forEach(observer => observer.onBuildDurationRentalFacility(updateFacility))
        } else {
            const newFacility: NewFacilityForDurationRental =
                new NewFacilityForDurationRental(
                    facilityCommon.sortOrder,
                    facilityCommon.categoryId,
                    facilityCommon.equipmentIds,
                    facilityCommon.facilityName,
                    facilityCommon.accessControlManager,
                    facilityCommon.cardKeyFacilityNumber,
                    facilityCommon.gateNumbers,
                    facilityCommon.images,
                    facilityCommon.facilityIntroductory,
                    facilityCommon.optionSettings,
                    facilityCommon.reservationSetting,
                    facilityCommon.releaseSetting,
                    facilityCommon.releaseStartDate,
                    facilityCommon.releaseStartTime,
                    facilityCommon.reserveBeforeCleaningTime,
                    facilityCommon.reserveAfterCleaningTime,
                    this.timeTableItems.map(timeTableItem => this.getCleansedBusinessSchedule(timeTableItem)),
                    this.getCleansedMaxReservationTimeSetting(),
                )
            this.newFacilityModelObservers
                .forEach(observer => observer.onBuildDurationRentalFacility(newFacility))
        }
    }

    formatTimeForSendServerFromCodeMasterInterface(
        hour: Nullable<CodeMasterInterface>,
        minute: Nullable<CodeMasterInterface>,
    ): string {
        const hourValue = valueStringOrNull(hour)
        const minuteValue = valueStringOrNull(minute)
        return formatServerTime(hourValue, minuteValue)
    }

    convertImageLocation(images: ImageLocation[]): ImageInformation[] {
        return images.map(image => ({
                fileNumber: image.fileNumber,
                url: image.url,
                name: null,
                size: null,
                base64data: null,
                md5: null,
            }),
        )
    }

    convertChargeUnitToUnitTime(chargeUnit: Nullable<CHARGE_UNIT>): Nullable<UNIT_TIME> {
        let unitTime: Nullable<UNIT_TIME>

        if (chargeUnit === null) {
            return null
        }
        if (chargeUnit === CHARGE_UNIT.FIXED) {
            unitTime = null
        } else if (chargeUnit === CHARGE_UNIT.TIME) {
            unitTime = UNIT_TIME.HOUR
        } else {
            unitTime = UNIT_TIME.MINUTES
        }

        return unitTime
    }

    convertReleaseStartDateTime(
        releaseStartDate: Nullable<string>,
        releaseStartTime: Nullable<RELEASE_START_HOURS>
    ): Nullable<string> {
        if (releaseStartDate === null) {
            return null
        }

        if (releaseStartTime === null) {
            return null
        }

        return `${releaseStartDate} ${releaseStartTime.value}:00`
    }
}

function getNotNullValue<T>(value: T | null): T {
    if (value === null) {
        throw Error
    }
    return value
}

function getValueOrDefault<T>(value: T | null, defaultValue: T): T {
    if (value === null) {
        return defaultValue
    }
    return value
}
